import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/lib/auth/AuthContext';
import { clientApi } from '@/config/axios';

const PERMISSIONS_QUERY_KEY = 'my-permissions';

export function usePermissions() {
  const { user, organization } = useAuth();

  const { data: permissions = [] } = useQuery<string[]>({
    queryKey: [PERMISSIONS_QUERY_KEY, organization?.organization_id],
    queryFn: async () => {
      const res = await clientApi.get('/api/v1/auth/me/permissions', {
        params: { org_id: organization!.organization_id },
      });
      return res.data;
    },
    enabled: !!user && !!organization?.organization_id,
    staleTime: 5 * 60 * 1000,
  });

  const permissionSet = useMemo(() => new Set(permissions), [permissions]);

  const can = useCallback(
    (permission: string) => user?.is_superuser || permissionSet.has(permission),
    [user?.is_superuser, permissionSet],
  );

  const canAny = useCallback(
    (...perms: string[]) => user?.is_superuser || perms.some((p) => permissionSet.has(p)),
    [user?.is_superuser, permissionSet],
  );

  const canAll = useCallback(
    (...perms: string[]) => user?.is_superuser || perms.every((p) => permissionSet.has(p)),
    [user?.is_superuser, permissionSet],
  );

  return { permissions, can, canAny, canAll, isAdmin: !!user?.is_superuser };
}
